import { useEffect, useRef, useState } from 'react';
import { Heart, ArrowUpDown, Check } from 'lucide-react';
import { reloadAfterMutation } from '../../lib/embedSafeReload';
import './ReactivateInjuredPlayerModal.css';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';

interface ReactivateInjuredPlayerModalProps {
  show: boolean;
  playerName: string;
  isGoalie: boolean;
  // Lists to allow selection for swap
  mainPlayers: any[];
  reservePlayers: any[];
  mainGoalies: any[];
  reserveGoalies: any[];
  hasReserveSpots: boolean;
  // Actions
  onReactivateDirectly: (destination: 'MAIN' | 'RESERVES') => Promise<{ success: boolean }> | { success: boolean };
  onReactivateWithSwap: (swapWithPlayerId: number, swapFromSection: 'MAIN' | 'RESERVES', destination: 'MAIN' | 'RESERVES') => Promise<{ success: boolean }> | { success: boolean };
  onClose: () => void;
  isProcessing: boolean;
  language: 'en' | 'fr';
  // Roster limit validation — when set, blocks direct reactivation to MAIN
  rosterLimitError?: string | null;
  // Standard Flexible : permet de choisir le joueur remplacé quand l'équipe est pleine
  allowReplacePlayer?: boolean;
  injuredPositionCode?: string;
  reserveFull?: boolean;
  onReactivateReplacing?: (replacedPlayerId: number) => Promise<{ success: boolean }> | { success: boolean };
}

export function ReactivateInjuredPlayerModal({
  show,
  playerName,
  isGoalie,
  mainPlayers,
  reservePlayers,
  mainGoalies,
  reserveGoalies,
  hasReserveSpots,
  onReactivateDirectly,
  onReactivateWithSwap,
  onClose,
  isProcessing,
  language,
  rosterLimitError,
  allowReplacePlayer = false,
  injuredPositionCode,
  reserveFull = false,
  onReactivateReplacing
}: ReactivateInjuredPlayerModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();
  const [showSwapOptions, setShowSwapOptions] = useState(false);
  const [showReplaceOptions, setShowReplaceOptions] = useState(false);
  const [replacedPlayerId, setReplacedPlayerId] = useState<number | null>(null);
  const [replaceNone, setReplaceNone] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<'MAIN' | 'RESERVES'>('MAIN');
  const [destination, setDestination] = useState<'MAIN' | 'RESERVES'>('MAIN');

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onClose]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setShowSwapOptions(false);
      setShowReplaceOptions(false);
      setReplacedPlayerId(null);
      setReplaceNone(false);
      setSelectedPlayerId(null);
      setSelectedSection('MAIN');
      setDestination('MAIN');
    }
  }, [show]);

  useEffect(() => {
    if (!hasReserveSpots && destination === 'RESERVES') {
      setDestination('MAIN');
    }
  }, [hasReserveSpots, destination]);

  if (!show) return null;

  const titleText = language === 'fr' ? 'Réactiver le Joueur Blessé' : 'Reactivate Injured Player';
  const questionText = language === 'fr' 
    ? `Comment voulez-vous réactiver ${playerName} ?`
    : `How do you want to reactivate ${playerName}?`;
  const directText = language === 'fr' ? 'Réactivation Directe' : 'Direct Reactivation';
  const swapText = language === 'fr' ? 'Réactivation avec Permutation' : 'Reactivation with Swap';
  const cancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const processingText = language === 'fr' ? 'Traitement en cours...' : 'Processing...';
  const directDesc = language === 'fr' 
    ? (destination === 'RESERVES' 
        ? 'Réactiver le joueur directement dans le club école (réserves)' 
        : 'Réactiver le joueur dans le club principal')
    : (destination === 'RESERVES'
        ? 'Reactivate player directly to the farm team (reserves)'
        : 'Reactivate player to the main club');
  const swapDesc = language === 'fr'
    ? 'Faire une permutation avec un joueur actif (sans coût de rétrogradation). Cela enverra le joueur choisi au club de réserve. Si vous souhaitez envoyer un joueur blessé en réserve, sélectionnez simplement un joueur déjà en réserve'
    : 'Swap with an active player (no demotion cost)';
  const directMainWarningText = language === 'fr'
    ? 'Si votre équipe est pleine, vous devez libérer un joueur de la même position avant de réintégrer ce joueur.'
    : 'If your team is full, you must release a player at the same position before reactivating this player.';
  const selectPlayerText = language === 'fr' ? 'Sélectionnez un joueur pour la permutation' : 'Select a player to swap with';
  const mainClubText = language === 'fr' ? 'Club Principal' : 'Main Club';
  const reservesText = language === 'fr' ? 'Réserves' : 'Reserves';
  const confirmSwapText = language === 'fr' ? 'Confirmer la Permutation' : 'Confirm Swap';
  const backText = language === 'fr' ? 'Retour' : 'Back';
  const noPlayersText = language === 'fr' ? 'Aucun joueur disponible' : 'No players available';
  const destinationText = language === 'fr' ? 'Destination du joueur réactivé :' : 'Destination for reactivated player:';
  const mainClubButtonText = language === 'fr' ? 'Club Principal' : 'Main Club';
  const reservesButtonText = language === 'fr' ? 'Club École (Réserves)' : 'Farm Team (Reserves)';

  function isSuccessResult(obj: any): obj is { success: boolean } {
    return obj && typeof obj === 'object' && 'success' in obj && typeof obj.success === 'boolean';
  }

  const handleDirectReactivation = async () => {
    const result = onReactivateDirectly(destination);
    if (result instanceof Promise) {
      const awaited = await result;
      if (isSuccessResult(awaited) && awaited.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
    } else if (isSuccessResult(result) && result.success) {
      setTimeout(() => {
        reloadAfterMutation();
      }, 1500);
    }
  };

  const handleSwapReactivation = async () => {
    if (!selectedPlayerId) return;
    
    const result = onReactivateWithSwap(selectedPlayerId, selectedSection, destination);
    if (result instanceof Promise) {
      const awaited = await result;
      if (isSuccessResult(awaited) && awaited.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
    } else if (isSuccessResult(result) && result.success) {
      setTimeout(() => {
        reloadAfterMutation();
      }, 1500);
    }
  };

  const canReplace = allowReplacePlayer && !!onReactivateReplacing;
  const teamIsFull = !!rosterLimitError;
  const replaceBlocked = hasReserveSpots && reserveFull;
  const replaceCandidates: any[] = isGoalie
    ? mainGoalies
    : mainPlayers.filter((p: any) => (injuredPositionCode === 'D' ? p.positioncode === 'D' : ['C', 'L', 'R'].includes(p.positioncode)));
  const replaceText = language === 'fr' ? 'Remplacer un joueur' : 'Replace a player';
  const replaceCardDesc = language === 'fr'
    ? 'Choisissez quel joueur de votre club principal quitte l’équipe pour faire de la place. Aucune signature ni rétrogradation n’est comptée.'
    : 'Choose which main club player leaves the team to make room. No signing or demotion is counted.';
  const replaceSelectText = language === 'fr' ? 'Quel joueur voulez-vous retirer à sa place ?' : 'Which player do you want to remove in their place?';
  const replaceNoCostText = language === 'fr'
    ? 'Cette action ne coûte aucune signature (mouvement) ni rétrogradation.'
    : 'This action costs no signing (move) and no demotion.';
  const replaceOutcomeText = hasReserveSpots
    ? (language === 'fr' ? 'Le joueur retiré sera envoyé dans votre club école (réserves).' : 'The removed player will be sent to your farm team (reserves).')
    : (language === 'fr' ? 'Le joueur retiré sera libéré (il n’y a pas de club école).' : 'The removed player will be released (there is no farm team).');
  const replaceBlockedText = language === 'fr'
    ? 'Remplacement impossible : envoyer un joueur en réserve ferait dépasser la limite de votre club école. Libérez d’abord un joueur de votre réserve.'
    : 'Replacement blocked: sending a player to reserves would exceed your farm team limit. Release a reserve player first.';
  const confirmReplaceText = language === 'fr' ? 'Confirmer le remplacement' : 'Confirm replacement';

  const noneText = language === 'fr' ? 'Aucun' : 'None';
  const noneDesc = language === 'fr'
    ? 'Réactiver le joueur dans l’alignement principal sans retirer personne'
    : 'Reactivate the player to the main lineup without removing anyone';
  const noneDisabledDesc = language === 'fr'
    ? 'Indisponible : votre alignement est déjà plein à cette position'
    : 'Unavailable: your lineup is already full at this position';
  const confirmNoneText = language === 'fr' ? 'Confirmer la réactivation' : 'Confirm reactivation';

  const openReplaceOptions = () => {
    setReplacedPlayerId(null);
    setReplaceNone(!teamIsFull);
    setShowReplaceOptions(true);
  };

  const handleReplaceReactivation = async () => {
    if (replaceNone) {
      const direct = await onReactivateDirectly('MAIN');
      if (isSuccessResult(direct) && direct.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
      return;
    }
    if (!replacedPlayerId || !onReactivateReplacing) return;
    const result = await onReactivateReplacing(replacedPlayerId);
    if (isSuccessResult(result) && result.success) {
      setTimeout(() => {
        reloadAfterMutation();
      }, 1500);
    }
  };

  const eligiblePlayers = isGoalie 
    ? (selectedSection === 'MAIN' ? mainGoalies : reserveGoalies)
    : (selectedSection === 'MAIN' ? mainPlayers : reservePlayers);

  return (
    <MyTeamsModal onClose={onClose} busy={isProcessing} anchorBias={0.6} preferredMaxHeight={470}>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" ref={modalRef}>
          <div className="modal-content bg-dark text-white reactivate-injured-modal" style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="modal-header border-secondary">
              <h5 className="modal-title d-flex align-items-center text-white">
                <span className="me-2 text-success"></span>
                {titleText}
              </h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-white">
              {showReplaceOptions ? (
                <>
                  <p className="mb-2 text-white fw-bold">{replaceSelectText}</p>
                  <div className="alert mb-3" style={{
                    backgroundColor: 'rgba(13, 110, 253, 0.2)',
                    border: '1px solid rgba(13, 110, 253, 0.3)',
                    color: '#ffffff'
                  }}>
                    <small className="d-block">{replaceNoCostText}</small>
                    {teamIsFull && <small className="d-block mt-1">{replaceOutcomeText}</small>}
                  </div>
                  {replaceBlocked && teamIsFull && !replaceNone && (
                    <div className="alert mb-3" style={{
                      backgroundColor: 'rgba(220, 53, 69, 0.15)',
                      border: '1px solid #dc3545',
                      color: '#ffffff'
                    }}>
                      <strong className="d-block mb-1" style={{ color: '#ff6b6b' }}>
                        {language === 'fr' ? 'Réactivation impossible' : 'Reactivation blocked'}
                      </strong>
                      <span style={{ color: '#f0f0f0' }}>{replaceBlockedText}</span>
                    </div>
                  )}
                  <div style={{ maxHeight: '300px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <div className="list-group mb-2">
                      <button
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center text-white${replaceNone ? ' reactivate-accent-row' : ''}`}
                        onClick={() => { setReplaceNone(true); setReplacedPlayerId(null); }}
                        disabled={isProcessing || teamIsFull}
                        aria-disabled={teamIsFull}
                        style={replaceNone ? {
                          backgroundColor: '#198754',
                          borderColor: '#20c997'
                        } : {
                          backgroundColor: '#212529',
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: teamIsFull ? 0.45 : 1
                        }}
                      >
                        <div className="flex-grow-1">
                          <div className="fw-bold">{noneText}</div>
                          <small className="text-white-50">{teamIsFull ? noneDisabledDesc : noneDesc}</small>
                        </div>
                      </button>
                    </div>
                    {replaceCandidates.length === 0 ? (
                      <div className="text-center text-white-50 py-4">{noPlayersText}</div>
                    ) : (
                      <div className="list-group">
                        {replaceCandidates.map((player: any) => (
                          <button
                            key={player.playerid}
                            type="button"
                            className={`list-group-item list-group-item-action d-flex align-items-center ${
                              replacedPlayerId === player.playerid ? 'text-white reactivate-accent-row' : 'bg-dark text-white border-secondary'
                            }`}
                            onClick={() => { setReplacedPlayerId(player.playerid); setReplaceNone(false); }}
                            disabled={isProcessing || !teamIsFull}
                            style={replacedPlayerId === player.playerid ? {
                              backgroundColor: '#fd7e14',
                              borderColor: '#ff922b'
                            } : {
                              backgroundColor: '#212529',
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: teamIsFull ? 1 : 0.45
                            }}
                          >
                            <img
                              src={`https://assets.nhle.com/mugs/nhl/20232024/${player.teamabbrevs}/${player.playerid}.png`}
                              alt={player.skaterfullname || player.goaliefullname}
                              className="rounded me-3"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.currentTarget.src = 'https://assets.nhle.com/mugs/nhl/latest/default-skater.png';
                              }}
                            />
                            <div className="flex-grow-1">
                              <div className="fw-bold">
                                {player.skaterfullname || player.goaliefullname}
                                {replacedPlayerId === player.playerid && (
                                  <span className="badge ms-2 reactivate-accent-badge" style={{ backgroundColor: '#ff922b', color: '#212529', fontWeight: 700 }}>
                                    {language === 'fr' ? 'Retiré' : 'Removed'}
                                  </span>
                                )}
                              </div>
                              <small className="text-white-50">
                                {player.positioncode} • {player.teamabbrevs} • ${player.salary?.toLocaleString()}
                              </small>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : !showSwapOptions ? (
                <>
                  <p className="mb-4 text-white">{questionText}</p>
                  
                  {/* Destination selector - Always shown */}
                  <div className="mb-4">
                    <label className="form-label text-white fw-bold">{destinationText}</label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${destination === 'MAIN' ? 'btn-primary btn-main-club' : 'btn-outline-primary'}`}
                        onClick={() => setDestination('MAIN')}
                      >
                        {mainClubButtonText}
                      </button>
                      {hasReserveSpots && (
                        <button
                          type="button"
                          className={`btn ${destination === 'RESERVES' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDestination('RESERVES')}
                        >
                          {reservesButtonText}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Roster limit error — blocks direct reactivation to MAIN */}
                  {rosterLimitError && destination === 'MAIN' && !canReplace && (
                    <div className="alert mb-4" style={{
                      backgroundColor: 'rgba(220, 53, 69, 0.15)',
                      border: '1px solid #dc3545',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}>
                      <strong className="d-block mb-1" style={{ color: '#ff6b6b' }}>
                        {language === 'fr' ? 'Réactivation impossible' : 'Reactivation blocked'}
                      </strong>
                      <span style={{ color: '#f0f0f0' }}>{rosterLimitError}</span>
                    </div>
                  )}

                  <div className="row g-3">
                    {canReplace && destination === 'MAIN' && (
                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-outline-warning w-100 d-flex flex-column align-items-center justify-content-center p-4 text-white"
                          onClick={openReplaceOptions}
                          disabled={isProcessing}
                        >
                          <ArrowUpDown size={32} className="mb-2" />
                          <strong className="text-white">{replaceText}</strong>
                          <small className="text-white-50 mt-1 text-center">{replaceCardDesc}</small>
                        </button>
                      </div>
                    )}

                    <div className="col-12 col-md-6">
                      <button
                        type="button"
                        className="btn btn-outline-success w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 text-white"
                        onClick={handleDirectReactivation}
                        disabled={isProcessing || !!(rosterLimitError && destination === 'MAIN')}
                        style={{ borderColor: '#198754', opacity: (rosterLimitError && destination === 'MAIN') ? 0.5 : 1 }}
                      >
                        <Check size={32} className="mb-2" />
                        <strong className="text-white">{directText}</strong>
                        <small className="text-white-50 mt-1 text-center">{directDesc}</small>
                        {destination === 'MAIN' && !rosterLimitError && (
                          <small className="text-warning mt-2 text-center">{directMainWarningText}</small>
                        )}
                      </button>
                    </div>
                    
                    {hasReserveSpots && (
                      <div className="col-12 col-md-6">
                        <button
                          type="button"
                          className="btn btn-outline-primary w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 text-white"
                          onClick={() => setShowSwapOptions(true)}
                          disabled={isProcessing || destination === 'RESERVES'}
                          style={{ 
                            borderColor: destination === 'RESERVES' ? '#6c757d' : '#0d6efd',
                            opacity: destination === 'RESERVES' ? 0.5 : 1,
                            cursor: destination === 'RESERVES' ? 'not-allowed' : 'pointer'
                          }}
                          title={destination === 'RESERVES' ? (language === 'fr' ? 'La permutation n\'est pas disponible pour les réserves' : 'Swap not available for reserves') : ''}
                        >
                          <ArrowUpDown size={32} className="mb-2" />
                          <strong className="text-white">{swapText}</strong>
                          <small className="text-white-50 mt-1 text-center">{swapDesc}</small>
                          {destination === 'RESERVES' && (
                            <small className="text-warning mt-2">
                              {language === 'fr' ? 'Non disponible pour les réserves' : 'Not available for reserves'}
                            </small>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-3 text-white">{selectPlayerText}</p>
                  
                  {/* Destination selector - Always shown */}
                  <div className="mb-3">
                    <label className="form-label text-white fw-bold">{destinationText}</label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${destination === 'MAIN' ? 'btn-primary btn-main-club' : 'btn-outline-primary'}`}
                        onClick={() => setDestination('MAIN')}
                      >
                        {mainClubButtonText}
                      </button>
                      {hasReserveSpots && (
                        <button
                          type="button"
                          className={`btn ${destination === 'RESERVES' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDestination('RESERVES')}
                        >
                          {reservesButtonText}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Section selector */}
                  <div className="btn-group w-100 mb-3" role="group">
                    <button
                      type="button"
                      className={`btn ${selectedSection === 'MAIN' ? 'btn-primary btn-main-club' : 'btn-outline-primary'}`}
                      onClick={() => {
                        setSelectedSection('MAIN');
                        setSelectedPlayerId(null);
                      }}
                    >
                      {mainClubText} ({isGoalie ? mainGoalies.length : mainPlayers.length})
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedSection === 'RESERVES' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => {
                        setSelectedSection('RESERVES');
                        setSelectedPlayerId(null);
                      }}
                    >
                      {reservesText} ({isGoalie ? reserveGoalies.length : reservePlayers.length})
                    </button>
                  </div>

                  {/* Player list */}
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {eligiblePlayers.length === 0 ? (
                      <div className="text-center text-white-50 py-4">
                        {noPlayersText}
                      </div>
                    ) : (
                      <div className="list-group">
                        {eligiblePlayers.map((player) => (
                          <button
                            key={player.playerid}
                            type="button"
                            className={`list-group-item list-group-item-action d-flex align-items-center ${
                              selectedPlayerId === player.playerid ? 'text-white reactivate-accent-row' : 'bg-dark text-white border-secondary'
                            }`}
                            onClick={() => setSelectedPlayerId(player.playerid)}
                            style={selectedPlayerId === player.playerid ? {
                              backgroundColor: '#fd7e14',
                              borderColor: '#ff922b'
                            } : {
                              backgroundColor: '#212529',
                              borderColor: 'rgba(255, 255, 255, 0.1)'
                            }}
                          >
                            <img
                              src={`https://assets.nhle.com/mugs/nhl/20232024/${player.teamabbrevs}/${player.playerid}.png`}
                              alt={player.skaterfullname || player.goaliefullname}
                              className="rounded me-3"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.currentTarget.src = 'https://assets.nhle.com/mugs/nhl/latest/default-skater.png';
                              }}
                            />
                            <div className="flex-grow-1">
                              <div className="fw-bold">
                                {player.skaterfullname || player.goaliefullname}
                                {selectedPlayerId === player.playerid && (
                                  <span
                                    className="badge ms-2 reactivate-accent-badge"
                                    style={{
                                      backgroundColor: '#ff922b',
                                      color: '#212529',
                                      fontWeight: 700
                                    }}
                                  >
                                    {language === 'fr' ? 'Sélectionné' : 'Selected'}
                                  </span>
                                )}
                              </div>
                              <small className={selectedPlayerId === player.playerid ? 'text-white-50' : 'text-white-50'}>
                                {player.positioncode} • {player.teamabbrevs} • ${player.salary?.toLocaleString()}
                              </small>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedPlayerId && (
                    <div className="alert alert-info mt-3 mb-0" style={{ 
                      backgroundColor: 'rgba(13, 110, 253, 0.2)',
                      borderColor: 'rgba(13, 110, 253, 0.3)',
                      color: '#ffffff'
                    }}>
                      <small>
                        {language === 'fr' 
                          ? '💡 Cette permutation ne coûtera pas de rétrogradation. Le joueur blessé prendra la place du joueur sélectionné.'
                          : '💡 This swap will not cost a demotion. The injured player will take the place of the selected player.'}
                      </small>
                    </div>
                  )}
                </>
              )}
              
              {isProcessing && (
                <div className="text-center mt-3 text-white">
                  <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                  {processingText}
                </div>
              )}
            </div>
            <div className="modal-footer border-secondary">
              {(showSwapOptions || showReplaceOptions) && (
                <button
                  type="button"
                  className="btn btn-secondary me-auto"
                  onClick={() => {
                    setShowSwapOptions(false);
                    setShowReplaceOptions(false);
                    setSelectedPlayerId(null);
                    setReplacedPlayerId(null);
                    setReplaceNone(false);
                  }}
                  disabled={isProcessing}
                >
                  {backText}
                </button>
              )}
              {showReplaceOptions && (replacedPlayerId || replaceNone) ? (
                <button
                  type="button"
                  className="btn reactivate-accent-btn"
                  style={{ backgroundColor: 'rgb(255, 146, 43)', color: 'rgb(33, 37, 41)', fontWeight: 700 }}
                  onClick={handleReplaceReactivation}
                  disabled={isProcessing || (!replaceNone && replaceBlocked)}
                >
                  {replaceNone ? confirmNoneText : confirmReplaceText}
                </button>
              ) : null}
              {showSwapOptions && selectedPlayerId ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSwapReactivation}
                  disabled={isProcessing}
                >
                  {confirmSwapText}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isProcessing}
              >
                {cancelText}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
        onClick={onClose}
      ></div>
    </MyTeamsModal>
  );
}
